import { useEffect, useState } from 'react'
import {
  Input,
  SimpleText,
  YStack,
  XStack,
  Image,
  ActionButton,
  ActiveButton,
  FinancialTable,
  CustomDatePickerControlled,
  CustomDropdown,
  View,
  XPDatePicker, XPReactNative, 
  TextInputComponent,
  Accordion,
  Paragraph,
  Square,
  Button,
  useToastController,
  DataLoadingDialog,
  useMedia,
  ScrollView
} from '@my/ui'
const { StyleSheet } = XPReactNative
import { color, formatDate, useAuthedRequestFn, useNavigate, RandomId, useSearchParams } from '@my/util'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
//import DateTimePickerModal from 'react-native-modal-datetime-picker'

import { scenarioDataTable, financialImpact, frequencyData, scenarioItems } from '../../helpers'
import { FinancialReviewHeading } from '../../../../components'
import { buttonStyle } from '../../../../utils/commonStyles'
import { CreateScenarioChart } from './createScenarioChart'
import images from '../../../../assets/images'
import { ScenarioFinancialProfileScreen, ScenarioProfileData } from '../scenarioFinancialProfile'
import { FinancialGoalsCore } from '../../../../components/modals/FinancialGoalModal'
import { ChevronDown } from '@tamagui/lucide-icons-2'
import { FinancialGoalsUtils } from '../../../../utils/financialGoals'
import { createGoalPayload } from '../../financialGoal/financialGoal'
import { InputField } from '../../../financial-profile/helpers/Helper'
import { Platform } from 'react-native'

// Constants
const PRIORITIES = ['High', 'Medium', 'Low']

export const CreateScenario = () => {
  
  const searchParams = useSearchParams();
  const [scenario, setScenario] = useState({})
  const [financial, setFinancial] = useState<string>('')
  const [frequency, setFrequency] = useState<string>('')
  const media = useMedia();

  const AccordionContentProps = {
    style: { backgroundColor: 'white', width: '100%' },
    padding: media['max-sm'] ? '18px 0px' : 18
  }
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    getValues
  } = useForm({defaultValues: scenario as any});

  const [scenarioProfileData, setScenarioProfileData] = useState<ScenarioProfileData>({ })
  const [formSubmitting, setFormSubmitting] = useState(false)


  const navigate = useNavigate();
  const toast = useToastController()
  const queryClient = useQueryClient()
  const authedRequestFn = useAuthedRequestFn()
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['financial-scenarios'],
    queryFn: () => authedRequestFn(`/financial-scenarios`),
  })

  useEffect(() => {
    const scenarioId = searchParams.get('scenarioId');
    const scenario = (data?.scenarios || []).find((scn) => scn.scenarioId === scenarioId);
    if (scenario) {
      setScenarioProfileData({ profile: scenario.profile, profileDelta: scenario.profileDelta });
      setValue('goalName', scenario.scenarioGoals[0].goalName);
      setValue('goalType' as any, scenario.scenarioGoals[0].category);
      setValue('targetAmount', scenario.scenarioGoals[0].targetAmount);
      setValue('timelineTimestamp' as any, scenario.scenarioGoals[0].targetDate);
      setValue('priority', scenario.scenarioGoals[0].priority);
      setValue('description' as any, scenario.scenarioGoals[0].description);
      setValue('scenarioId' as any, scenarioId);
    }
  }, [data])

  const postScenario = useMutation({
    mutationFn: (formData: any) => {
      const values = getValues();
      
      const goalTypeFromDate = FinancialGoalsUtils.goalCategoryFromTargetDate(values['timelineTimestamp'])
      const payload = createGoalPayload(values, {}, goalTypeFromDate)
            
      const scenarioId = formData.scenarioId || RandomId();
      const body = { scenarioGoals: [payload], ...formData, scenarioId};
      //console.log(body);
      return authedRequestFn(`/financial-scenarios/${scenarioId}`, { method: 'PUT', body })
    },
    onMutate: () => setFormSubmitting(true),
    onSuccess: () => {
      toast.show('Successfully Saved!');
      queryClient.invalidateQueries({ queryKey: ['financial-scenarios'] });
      //navigate('/scenario-planning');
    },
    onSettled: () => {
      setFormSubmitting(false);
      window.scrollTo(0,0);
      navigate('/scenario-planning');
    },
    onError: () => {
      toast.show('Error while saving...');
    },
  })

  // Render text input components with validation
  const renderTextInput = (
    name: keyof typeof getValues,
    placeholder: string,
    rules?: any,
    props?: any
  ) => {
    return (

      <InputField
        control={control}
        name={name}
        rules={rules}
        placeholder={placeholder}
        showPlaceholder
        disableLabel
      />
    )
  }
  // Render priority buttons for selecting goal priority
  const renderPriorityButtons = () => (
    <Controller
      control={control}
      name="priority"
      rules={{ required: 'Priority level is required' }}
      render={({ field: { onChange, value } }) => (
        <XStack marginVertical={10} gap={10}>
          {PRIORITIES.map((item) => (
            <ActiveButton
              key={item}
              //unstyled
              style={{ width: 100, height: 34, padding: 0}}
              variant={value === item ? undefined : 'outlined'}
              onPress={() => onChange(item)}
              ButtonTextProps={{children: item}}
            />
          ))}
        </XStack>
      )}
    />
  )

  const onCreateHandler = async () => {
    await postScenario.mutateAsync(scenarioProfileData)
  }

  const output = (
    <YStack gap={20} padding={'$4'}>
      {/* Header */}
      <FinancialReviewHeading
        title="Scenario Planning"
        titleLink="/scenario-planning"
        type="Create Scenario"
        img={images.scenarioPlanning}
        imageStyle={{
          //'$platform-native': { width: 15, height: 19 },
          //'$platform-web': { width: 20, height: 26 },
        }}
      />

      <Accordion
        style={{ overflow: "hidden", backgroundColor: 'white'}} 
        type="multiple" defaultValue={['a1']}
      >
        <Accordion.Item value="a1">
          <Accordion.Trigger style={{
            flexDirection:"row", justifyContent: "space-between", borderWidth: 1, borderStyle: 'solid', borderColor: 'rgb(240,240,240)'}}
          >
            {({ open }: { open: boolean }) => (
              <>
                {/* <Paragraph>1. Define your scenario goal</Paragraph> */}
                <SimpleText color={open ? color.tzaGreen : 'rgb(73, 80, 87)'} fontSize={20} fontWeight={'700'}>
                  1. Define your scenario goal
                </SimpleText>
                <Square transition="quick" rotate={open ? '180deg' : '0deg'}>
                  <ChevronDown size="$1" />
                </Square>
              </>
            )}
          </Accordion.Trigger>
          <Accordion.HeightAnimator transition="medium">
            <Accordion.Content 
              {...AccordionContentProps}
              //transition="medium" 
              //exitStyle={{ opacity: 0 }}
            >
              
              <FinancialGoalsCore 
                control={control} 
                handleSubmit={handleSubmit}
                handleSubmitForm={setScenario}
                renderTextInput={renderTextInput}
                renderPriorityButtons={renderPriorityButtons} 
                hideActionButtons={true}
                labels={{
                  header: '',
                  name: 'Name',
                }}
                hideHeader={true}
              />
            </Accordion.Content>
          </Accordion.HeightAnimator>
        </Accordion.Item>

        <Accordion.Item value="a2">
          <Accordion.Trigger style={{
            flexDirection:"row", justifyContent: "space-between", borderWidth: 1, borderStyle: 'solid', borderColor: 'rgb(240,240,240)'}}
          >
            {({
              open,
            }: {
              open: boolean
            }) => (
              <>
                <SimpleText color={open ? color.tzaGreen : 'rgb(73, 80, 87)'} fontSize={20} fontWeight={'700'}>2. Customize your Financial Profile (optional)</SimpleText>
                <Square transition="quick" rotate={open ? '180deg' : '0deg'}>
                  <ChevronDown size="$1" />
                </Square>
              </>
            )}
          </Accordion.Trigger>
          <Accordion.HeightAnimator transition="medium">
            <Accordion.Content 
              {...AccordionContentProps}/* transition="medium" exitStyle={{ opacity: 0 }} */
            >
              <ScenarioFinancialProfileScreen {...{scenarioProfileData, setScenarioProfileData}} />
            </Accordion.Content>
          </Accordion.HeightAnimator>
        </Accordion.Item>
      </Accordion>
      <YStack style={{alignItems: 'center'}}>
        <ActiveButton 
          style={{width: 160, padding: 0}}
          onPress={onCreateHandler}
          ButtonTextProps={{children: 'Create Scenario'}}
        />
      </YStack>

      <DataLoadingDialog open={formSubmitting} onOpenChange={setFormSubmitting} />
    </YStack>
  );
  
  return (
    Platform.OS === 'web' ? (
      output
    ) : (
      <ScrollView backgroundColor={color.white}>
        {output}
      </ScrollView>
    )
  );
}




/* const styles = StyleSheet.create({
  modalContainer: { flex: 1 },
  headerContainer: { width: '100%', marginBottom: 20 },
  headerTitleContainer: { width: '90%', alignItems: 'center' },
  headerTitle: { fontWeight: '700', fontSize: 20, color: color.tzaGreen },
  closeButton: { position: 'absolute', right: 5, top: 5 },
  closeIcon: { height: 17, width: 17 },
  inputContainer: { paddingHorizontal: 10 },
  label: { fontSize: 16, fontWeight: '700', color: color.grey },
  datePickerButton: { borderBottomWidth: 2, borderBottomColor: '#85C19D' },
  dateText: { fontWeight: '600', fontSize: 17 },
  dateTimePicker: {
    alignSelf: 'flex-start',
    backgroundColor: 'white',
    opacity: 0.5,
    color: color.black,
    flex: 1,
  },
  buttonGroupContainer: { width: '60%', marginTop: '5%' },
  buttonGroup: { gap: 20, paddingHorizontal: 10 },
  saveButton: { backgroundColor: '#85C19D', color: '#FFF', fontSize: 17 },
  cancelButton: { color: '#848484', borderColor: '#848484', fontSize: 17 },
}) */
